import { type AdminUser, CSRF_HEADER, type EsiCredentialState } from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { InlineError, Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { ApiError, noContent, ok } from "../api-errors.ts";
import { t as translate } from "../i18n/i18n.ts";
import { useSession } from "../session.tsx";
import { useAdminApi } from "./admin-api.tsx";
import { adminKeys } from "./entity-tree.ts";

/**
 * A person's ESI sign-in beside their SSO, in their Users dialog (openspec change
 * `sso-and-esi-credentials`, D4, 3.6; tenant admins): held and allowed by the domain, "Send ESI
 * sign-in link" for an eligible person without one, "Revoke link" while a sent link is not used
 * yet (user, 2026-11-03), and "Remove ESI sign-in" for a holder. The state is in the user's
 * detail only (listings omit it); nothing shows for a person whose domain allows none and who
 * holds none and has none pending.
 */
export function EsiCredentialAdmin({ user }: { user: Pick<AdminUser, "id" | "email" | "status"> }) {
  useTranslation();
  const id = useId();
  const { admin } = useAdminApi();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  /** The destructive action being confirmed in place, then the last one done (its notice). */
  const [confirming, setConfirming] = useState<"remove" | "revoke" | null>(null);
  const [done, setDone] = useState<"remove" | "revoke" | null>(null);
  const detail = useQuery({
    queryKey: [...adminKeys.users, "detail", user.id],
    queryFn: async () => ok(await admin.users[":userId"].$get({ param: { userId: user.id } })),
    retry: false,
  });
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: [...adminKeys.users, "detail", user.id] });
  const headers = { headers: { [CSRF_HEADER]: csrfToken } };
  const send = useMutation({
    mutationFn: async () =>
      ok(
        await admin.users[":userId"]["esi-credential"].$post(
          { param: { userId: user.id } },
          headers,
        ),
      ),
    // The detail then says a link is pending, with its revocation.
    onSuccess: () => void refresh(),
  });
  // Removing a held credential and revoking a pending link are the same request (D4).
  const remove = useMutation({
    mutationFn: async () =>
      noContent(
        await admin.users[":userId"]["esi-credential"].$delete(
          { param: { userId: user.id } },
          headers,
        ),
      ),
    onSuccess: () => {
      setDone(confirming);
      setConfirming(null);
      void refresh();
    },
  });

  const state: EsiCredentialState | undefined = detail.data?.esiCredential;
  if (!state || (!state.held && !state.allowed && !state.pending)) {
    return null;
  }
  const error = send.error ?? remove.error;
  return (
    <section
      data-esi-content="people"
      aria-labelledby={`${id}-esi`}
      className="flex flex-col gap-3"
    >
      <h3 id={`${id}-esi`} className="text-sm font-semibold text-fg">
        {translate("users.esiCredential.title")}
      </h3>
      <p className="text-sm text-fg-secondary">
        {state.held
          ? state.allowed
            ? translate("users.esiCredential.held")
            : translate("users.esiCredential.heldNotAllowed")
          : state.pending
            ? translate("users.esiCredential.pending")
            : translate("users.esiCredential.none")}
      </p>
      {send.isSuccess && !done ? (
        <Notice tone="success" live>
          {translate("users.esiCredential.sent", { email: send.data.sentTo })}
        </Notice>
      ) : null}
      {remove.isSuccess && done ? (
        <Notice tone="success" live>
          {done === "revoke"
            ? translate("users.esiCredential.revoked")
            : translate("users.esiCredential.removed")}
        </Notice>
      ) : null}
      {error ? <InlineError>{refusal(error)}</InlineError> : null}
      {confirming ? (
        <div className="flex flex-col gap-3 rounded-lg border border-line p-3">
          <p className="text-sm text-fg-secondary">
            {confirming === "revoke"
              ? translate("users.esiCredential.revokeEffect", { email: user.email })
              : translate("users.esiCredential.removeEffect", { email: user.email })}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
            >
              {confirming === "revoke"
                ? translate("users.esiCredential.revoke")
                : translate("users.esiCredential.remove")}
            </Button>
            <Button variant="ghost" disabled={remove.isPending} onClick={() => setConfirming(null)}>
              {translate("app.cancel")}
            </Button>
          </div>
        </div>
      ) : state.held || state.pending ? (
        <Button
          variant="outline"
          className="self-start"
          onClick={() => {
            remove.reset();
            send.reset();
            setDone(null);
            setConfirming(state.held ? "remove" : "revoke");
          }}
        >
          {state.held
            ? translate("users.esiCredential.removeEllipsis")
            : translate("users.esiCredential.revokeEllipsis")}
        </Button>
      ) : user.status === "active" && state.allowed && !send.isSuccess ? (
        <Button
          variant="outline"
          className="self-start"
          disabled={send.isPending}
          onClick={() => {
            remove.reset();
            setDone(null);
            send.mutate();
          }}
        >
          {send.isPending
            ? translate("users.esiCredential.sending")
            : translate("users.esiCredential.send")}
        </Button>
      ) : null}
    </section>
  );
}

/** A refused send or removal, in plain words. */
function refusal(error: Error): string {
  if (!(error instanceof ApiError)) {
    return translate("errors.network");
  }
  if (error.status === 429 || error.code === "throttled") {
    return translate("users.esiCredential.refused.throttled");
  }
  if (error.status === 403) {
    return translate("users.esiCredential.refused.notAllowed");
  }
  if (error.code === "last_sign_in_method") {
    return translate("users.esiCredential.refused.onlyWayIn");
  }
  if (error.code === "conflict") {
    return translate("users.esiCredential.refused.conflict");
  }
  return error.message;
}
